import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const joined = useRef(!!sessionStorage.getItem('brayo_newsletter'));

  useEffect(() => {
    if (joined.current) return;
    let alive = true;
    let timer;
    const initial = setTimeout(() => setOpen(true), 4000);
    const loop = () => {
      timer = setTimeout(() => {
        if (!joined.current) setOpen(true);
        if (alive) loop();
      }, 600000 + Math.random() * 300000);
    };
    loop();
    return () => { alive = false; clearTimeout(initial); clearTimeout(timer); };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    if (!email) return;
    sessionStorage.setItem('brayo_newsletter', '1');
    joined.current = true;
    setOpen(false);
    toast.success('You are on the list.');
    api.post('/submissions', { name: 'Newsletter', email, department: 'Newsletter' }).catch(() => {});
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="newsletter-popup"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-brayo-charcoal/40 p-5 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.94, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 24, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto border border-brayo-line bg-brayo-paper p-8 text-center shadow-[0_40px_90px_-40px_rgba(22,22,22,0.4)] md:p-10"
          >
            <button
              data-testid="newsletter-popup-close"
              onClick={() => setOpen(false)}
              aria-label="Close newsletter popup"
              className="meta-label absolute right-4 top-4 flex h-11 w-11 items-center justify-center border border-brayo-line text-brayo-dim transition-colors hover:border-brayo-blue hover:text-brayo-blue"
            >
              ✕
            </button>

            <img src="/brand/brayo-logo-alpha.png" alt="Brayo Records" className="mx-auto h-12" />
            <p className="meta-label mt-8 text-brayo-blue">Newsletter</p>
            <h2 className="display-title mt-3 text-3xl text-brayo-charcoal md:text-4xl">
              Join the <span className="text-brayo-blue">Brayo</span> list
            </h2>
            <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-brayo-mute">
              New releases, tour dates and label news — first, straight to your inbox. No noise.
            </p>

            <form data-testid="newsletter-popup-form" onSubmit={submit} className="mt-7 flex border border-brayo-line bg-white">
              <input
                data-testid="newsletter-popup-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent px-4 py-3.5 text-base md:text-sm text-brayo-charcoal placeholder:text-brayo-dim focus:outline-none"
              />
              <button data-testid="newsletter-popup-join-btn" type="submit" className="meta-label shrink-0 bg-brayo-blue px-5 text-white transition-colors hover:bg-brayo-charcoal">
                Join
              </button>
            </form>
            <p className="meta-label mt-5 text-brayo-dim">One email a month, at most.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}